import React, { useContext, useMemo } from 'react';
import PageLayout from '../pagelayout';
import { useIntl } from 'react-intl';
import translation from './activity.psa.i18n';
import { Grid } from '@mui/material';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import { useLoader } from '../api/useLoader';
import { PraksisaktivitetStatusDto } from '../api/generated/praksissted-generated-api';
import { useNavigate, useParams } from 'react-router-dom';
import ActivityInfo from './activityInfo.psa';
import ActivityTabs from './activityTabs.psa';
import PanelHeader from '../components/panelheader/panelHeader';
import Button from '../components/button';
import { Alert } from '../components/alert';
import statusTranslations from '../commontranslations/psaStatuses.i18n';
import {
  ActivityContextPSA,
  ActivityContextPSAType,
  useActivityContextPSA,
} from './activitycontexts';
import { useFormatMessageFor } from '../i18n/translation';
import Colors from '../alpColors';

const Activity = () => {
  const { formatMessage } = useIntl();
  const { activityId } = useParams<{ activityId: string }>();
  const { praksiskalenderApi } = useContext(PraksisstedApiContext);
  const { data: praksisaktivitet, reload: reloadPraksisaktivitet } = useLoader(
    () =>
      praksiskalenderApi.praksisaktivitetIdGet({
        pathParams: { id: activityId! }, // eslint-disable-line @typescript-eslint/no-non-null-assertion
      }),
    [activityId],
  );

  const activityContextPSAValue = useMemo(
    () => ({ praksisaktivitet, reloadPraksisaktivitet }),
    [praksisaktivitet, reloadPraksisaktivitet],
  );

  if (activityContextPSAValue.praksisaktivitet) {
    return (
      <ActivityContextPSA.Provider
        value={activityContextPSAValue as ActivityContextPSAType}
      >
        <ActivityContent />
      </ActivityContextPSA.Provider>
    );
  }

  const loadingText = formatMessage({ id: translation.loading.id });

  return (
    <PageLayout isIndented={true} title={loadingText} subtitle={loadingText} />
  );
};

const useStatusLabel = (
  status: PraksisaktivitetStatusDto | undefined,
): string => {
  const labels = useFormatMessageFor(translation);
  const statusLabels = useFormatMessageFor(statusTranslations);

  switch (status) {
    case PraksisaktivitetStatusDto.KanBekreftes:
      return statusLabels.kanBekreftes;
    case PraksisaktivitetStatusDto.BekreftetMenManglerKapasitet:
      return statusLabels.manglerKapasitet;
    case PraksisaktivitetStatusDto.BekreftetOverstyrtKapasitet:
      return statusLabels.overstyrtKapasitet;
    case PraksisaktivitetStatusDto.BekreftetOgHarKapasitet:
      return statusLabels.harKapasitet;
    case PraksisaktivitetStatusDto.BekreftetMenManglerFordeling:
      return statusLabels.manglerFordeling;
    case PraksisaktivitetStatusDto.KlarForGjennomfoering:
      return statusLabels.klarForGjennomfoering;
    case PraksisaktivitetStatusDto.Gjennomfoert:
      return statusLabels.gjennomfoert;
    case PraksisaktivitetStatusDto.Avtalefeil:
      return statusLabels.avtalefeil;
    case undefined:
      return labels.activitytitle;
  }
};

const getStatusBackground = (
  status: PraksisaktivitetStatusDto | undefined,
): string => {
  switch (status) {
    case PraksisaktivitetStatusDto.BekreftetMenManglerFordeling:
    case PraksisaktivitetStatusDto.KanBekreftes:
    case PraksisaktivitetStatusDto.Avtalefeil:
      return Colors.mojo;
  }
  return Colors.fiord;
};

const ActivityContent = () => {
  const navigate = useNavigate();
  const labels = useFormatMessageFor(translation);
  const {
    praksisaktivitet: {
      status,
      publisertTilFs,
      praksis: { navn: praksisnavn },
    },
  } = useActivityContextPSA();
  const statusLabel = useStatusLabel(status);
  const statusBackground = getStatusBackground(status);

  return (
    <PageLayout
      isIndented
      title={`${labels.title}${praksisnavn.toLowerCase()}`}
      subtitle={labels.subtitle}
      actionItems={
        <Button.Tertiary onClick={() => navigate('/calendar')}>
          {labels.tilbake}
        </Button.Tertiary>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PanelHeader
            backgroundColor={statusBackground}
            color={'white'}
            title={statusLabel}
            padLeft
          />
          <ActivityInfo />
          <ActivityTabs />
          {publisertTilFs && <Alert>{labels.publisertTilFs}</Alert>}
        </Grid>
      </Grid>
    </PageLayout>
  );
};

export default Activity;
